import type { StartingCrystalPick } from '@sorrem/shared';
import { useCallback, useMemo } from 'react';
import {
    leavePartyCrucible,
    removePartyCrucible,
    removePartyMember,
    setPartyReady,
    stagePartyStartingCrystals,
    startPartyCrucible,
    usePartyRealtimeSelector,
    type PartyRealtimeState,
} from './party-realtime-store';
import { isShallowEqual } from '@sorrem-client/ui/hooks';
import { getStoredAccountId } from '@sorrem-client/ui/shared/auth-session';

/**
 * The party store's fields the crucible page reads. The chat is left out: the
 * page's `PartyChatProvider` reads it, so a message doesn't re-render the page.
 */
type CruciblePartySelection = Pick<
    PartyRealtimeState,
    'partyState' | 'isConnected' | 'readyPayload' | 'crucibleStartedAtMs' | 'startCountdownEndsAtMs' | 'partyIssue' | 'startNotice'
>;

interface UseCruciblePartyState extends CruciblePartySelection {
    /** Account ID of the currently logged-in player. */
    myAccountId: string | null;
    /** Signal the server to start the crucible at once, skipping the ready-up (creator only; direct-entry runs). */
    startCrucible: () => Promise<void>;
    /** Leave the party and disconnect from the party channel. */
    leaveCrucibleParty: () => Promise<boolean>;
    /** Remove the current crucible (creator only). */
    removeCrucibleParty: () => Promise<boolean>;
    /** Remove one other member from the party by character id (creator only). */
    removeMember: (characterId: string) => Promise<boolean>;
    /** Replaces the crystals this member has coalesced on the gathering page; resolves with null, or why the server refused. */
    stageStartingCrystals: (picks: StartingCrystalPick[]) => Promise<string | null>;
    /** Marks this member ready (or not) to enter; resolves with null, or why the server refused. */
    setReady: (isReady: boolean) => Promise<string | null>;
}

export function useCrucibleParty(crucibleId: string | null): UseCruciblePartyState {
    const {
        partyState,
        isConnected,
        readyPayload,
        crucibleStartedAtMs,
        startCountdownEndsAtMs,
        partyIssue,
        startNotice,
    } = usePartyRealtimeSelector<CruciblePartySelection>(
        crucibleId,
        (storeState) => ({
            partyState: storeState.partyState,
            isConnected: storeState.isConnected,
            readyPayload: storeState.readyPayload,
            crucibleStartedAtMs: storeState.crucibleStartedAtMs,
            startCountdownEndsAtMs: storeState.startCountdownEndsAtMs,
            partyIssue: storeState.partyIssue,
            startNotice: storeState.startNotice,
        }),
        isShallowEqual,
    );
    const myAccountId = getStoredAccountId();

    const startCrucible = useCallback(async (): Promise<void> => {
        if (!crucibleId) {
            return;
        }

        await startPartyCrucible(crucibleId);
    }, [crucibleId]);

    const leaveCrucibleParty = useCallback(async (): Promise<boolean> => {
        if (!crucibleId) {
            return false;
        }

        return await leavePartyCrucible(crucibleId);
    }, [crucibleId]);

    const removeCrucibleParty = useCallback(async (): Promise<boolean> => {
        if (!crucibleId) {
            return false;
        }
        return await removePartyCrucible(crucibleId);
    }, [crucibleId]);

    const removeMember = useCallback(async (characterId: string): Promise<boolean> => {
        if (!crucibleId) {
            return false;
        }
        return await removePartyMember(crucibleId, characterId);
    }, [crucibleId]);

    const stageStartingCrystals = useCallback(async (picks: StartingCrystalPick[]): Promise<string | null> => {
        if (!crucibleId) {
            return 'Not in a crucible party.';
        }
        return await stagePartyStartingCrystals(crucibleId, picks);
    }, [crucibleId]);

    const setReady = useCallback(async (isReady: boolean): Promise<string | null> => {
        if (!crucibleId) {
            return 'Not in a crucible party.';
        }
        return await setPartyReady(crucibleId, isReady);
    }, [crucibleId]);

    return useMemo(() => ({
        myAccountId,
        partyState,
        isConnected,
        readyPayload,
        crucibleStartedAtMs,
        startCountdownEndsAtMs,
        partyIssue,
        startNotice,
        startCrucible,
        leaveCrucibleParty,
        removeCrucibleParty,
        removeMember,
        setReady,
        stageStartingCrystals,
    }), [
        startCountdownEndsAtMs,
        crucibleStartedAtMs,
        isConnected,
        leaveCrucibleParty,
        myAccountId,
        partyIssue,
        partyState,
        readyPayload,
        startNotice,
        removeCrucibleParty,
        removeMember,
        setReady,
        stageStartingCrystals,
        startCrucible,
    ]);
}
